import React, { useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import LeadOutcomeModal from './LeadOutcomeModal';

const TIER_CONFIG = {
  hot:  { label: '🔥 Hot',   badgeClass: 'badge-red',    desc: 'Recent activity, high engagement' },
  warm: { label: '🌡️ Warm', badgeClass: 'badge-orange', desc: 'Some recent activity' },
  cool: { label: '🧊 Cool',  badgeClass: 'badge-blue',   desc: 'Infrequent activity' },
  cold: { label: '❄️ Cold',  badgeClass: 'badge-gray',   desc: 'No recent activity' },
};

const PIPELINE_CONTACT_METHOD = {
  'Lead Engine':          ['Text', 'Call', 'Email'],
  'Win Back Non-Member':  ['Text', 'Call', 'Email'],
  'Win Back Past Member': ['Call', 'Text', 'Email'],
  'Trial Ready':          ['Call', 'Text', 'Email'],
};

// ─── Activity Panel (GAP-L6) ──────────────────────────────────────────────────

function ActivityPanel({ activity }) {
  if (!activity) return null;

  const { totalVisits, visits30d, visits90d, daysSinceLastVisit, lastVisitDate, recentSessions } = activity;

  const urgencyColor = daysSinceLastVisit === null ? '#6b7280'
    : daysSinceLastVisit <= 7  ? '#22c55e'
    : daysSinceLastVisit <= 21 ? '#f97316'
    : '#ef4444';

  const urgencyLabel = daysSinceLastVisit === null ? 'No visits on file'
    : daysSinceLastVisit === 0 ? 'Visited today'
    : daysSinceLastVisit === 1 ? '1 day ago'
    : `${daysSinceLastVisit} days ago`;

  const sessionTypeColors = {
    'Reservation': '#3b82f6',
    'Program':     '#8b5cf6',
    'Clinic':      '#f97316',
    'Open Play':   '#22c55e',
  };

  return (
    <div style={{
      background: 'var(--surface-2)', border: '1px solid var(--border)',
      borderRadius: '10px', padding: '0.75rem 1rem',
      marginBottom: '0.75rem',
    }}>
      {/* Section header */}
      <div style={{
        fontSize: 11, fontWeight: 700, color: '#666',
        textTransform: 'uppercase', letterSpacing: '0.06em',
        marginBottom: 10,
      }}>
        📊 Activity Signal
      </div>

      {/* KPI row */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 8, marginBottom: 12,
      }}>
        {[
          { label: 'Total Visits', value: totalVisits,                color: 'var(--text)' },
          { label: 'Last 30 Days', value: visits30d,                  color: visits30d > 0 ? '#22c55e' : '#6b7280' },
          { label: 'Last 90 Days', value: visits90d,                  color: 'var(--text)' },
          { label: 'Days Since',   value: daysSinceLastVisit ?? '—',  color: urgencyColor },
        ].map(k => (
          <div key={k.label} style={{
            background: '#0a0a0a', border: '1px solid #1a1a1a',
            borderRadius: 6, padding: '6px 8px', textAlign: 'center',
          }}>
            <div style={{ fontSize: 9, color: '#555', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3 }}>
              {k.label}
            </div>
            <div style={{ fontSize: 16, fontWeight: 800, color: k.color, fontFamily: 'DM Mono, monospace' }}>
              {k.value}
            </div>
          </div>
        ))}
      </div>

      {/* Last visit urgency strip */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '5px 8px', borderRadius: 5,
        background: urgencyColor + '11',
        border: `1px solid ${urgencyColor}33`,
        marginBottom: recentSessions?.length > 0 ? 10 : 0,
      }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: urgencyColor }}>
          Last visit: {urgencyLabel}
        </span>
        {lastVisitDate && (
          <span style={{ fontSize: 11, color: '#555' }}>· {lastVisitDate}</span>
        )}
      </div>

      {/* Recent sessions */}
      {recentSessions?.length > 0 && (
        <div>
          <div style={{ fontSize: 10, color: '#555', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
            Recent Sessions
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {recentSessions.map((s, i) => {
              const typeColor = sessionTypeColors[s.type] || '#6b7280';
              return (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '4px 8px',
                  background: '#0a0a0a', border: '1px solid #1a1a1a',
                  borderRadius: 5,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      background: typeColor + '22', color: typeColor,
                      border: `1px solid ${typeColor}44`,
                      borderRadius: 3, padding: '1px 6px',
                      fontSize: 10, fontWeight: 700,
                    }}>
                      {s.type || 'Visit'}
                    </span>
                    {s.source && (
                      <span style={{ fontSize: 10, color: '#444' }}>{s.source}</span>
                    )}
                  </div>
                  <span style={{ fontSize: 11, color: '#555', fontFamily: 'DM Mono, monospace' }}>
                    {s.date}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {totalVisits === 0 && (
        <div style={{ fontSize: 12, color: '#555', fontStyle: 'italic', textAlign: 'center', padding: '8px 0' }}>
          No visit history on file
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function LeadProspectDetail({ prospectId, onBack }) {
  const { get }   = useApi();
  const [prospect, setProspect]       = useState(null);
  const [loading, setLoading]         = useState(true);
  const [outcomeModal, setOutcomeModal] = useState(false);
  const [showDNCConfirm, setShowDNCConfirm] = useState(false);

  function load() {
    setLoading(true);
    get(`/lead/prospect/${prospectId}`)
      .then(setProspect)
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, [prospectId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="empty-state"><div className="spinner" /></div>;
  if (!prospect) return <div className="empty-state"><p>Prospect not found.</p></div>;

  const tier         = prospect.tier?.toLowerCase();
  const tierCfg      = TIER_CONFIG[tier] || null;
  const contactOrder = PIPELINE_CONTACT_METHOD[prospect.pipeline] || ['Text', 'Call', 'Email'];

  const daysSinceContact = prospect.outcomeDate
    ? Math.round((new Date() - new Date(prospect.outcomeDate)) / 86400000)
    : null;

  return (
    <div style={{ maxWidth: 680 }}>

      {/* Header */}
      <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="btn btn-ghost btn-sm" onClick={onBack}>← Back</button>
        <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', textTransform: 'capitalize' }}>{prospect.name}</span>
        {tierCfg && <span className={`badge ${tierCfg.badgeClass}`}>{tier}</span>}
        <span className="badge badge-gray">{prospect.pipeline}</span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
          <button className="btn btn-primary btn-sm" onClick={() => setOutcomeModal(true)}>
            Log Contact
          </button>
          <button
            className="btn btn-ghost btn-sm"
            style={{ color: 'var(--red)', borderColor: 'var(--red)', fontSize: '0.78rem' }}
            onClick={() => setShowDNCConfirm(true)}
          >
            DNC
          </button>
        </span>
      </div>

      {/* Contact method guidance */}
      <div style={styles.contactBar}>
        <span style={{ color: 'var(--text-secondary)', fontSize: '0.78rem' }}>Contact order:</span>
        <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>{contactOrder.join(' → ')}</span>
      </div>

      {/* Score + tier card */}
      <div style={styles.scoreCard}>
        <div style={styles.scoreMain}>
          <div style={styles.scoreNumber}>{prospect.score ?? '—'}</div>
          <div style={styles.scoreLabel}>Lead Score</div>
        </div>
        <div style={styles.scoreDivider} />
        <div style={styles.scoreDetails}>
          {tierCfg && (
            <div style={styles.scoreRow}>
              <span style={styles.scoreKey}>Tier</span>
              <span className={`badge ${tierCfg.badgeClass}`}>{tier}</span>
            </div>
          )}
          {tierCfg && (
            <div style={styles.scoreRow}>
              <span style={styles.scoreKey}>Signal</span>
              <span style={styles.scoreVal}>{tierCfg.desc}</span>
            </div>
          )}
          <div style={styles.scoreRow}>
            <span style={styles.scoreKey}>Membership</span>
            <span style={styles.scoreVal}>{prospect.membershipType || '—'}</span>
          </div>
          <div style={styles.scoreRow}>
            <span style={styles.scoreKey}>Level</span>
            <span style={styles.scoreVal}>{prospect.skillLevel || '—'}</span>
          </div>
          {prospect.duprRating && (
            <div style={styles.scoreRow}>
              <span style={styles.scoreKey}>DUPR</span>
              <span style={styles.scoreVal}>{prospect.duprRating}</span>
            </div>
          )}
        </div>
      </div>

      {/* Activity panel (GAP-L6) */}
      <ActivityPanel activity={prospect.activity} />

      {/* Contact info */}
      <div style={styles.infoCard}>
        <div style={styles.infoGrid}>
          {prospect.phone && (
            <div style={styles.infoRow}><span style={styles.infoLabel}>Phone</span><span>{prospect.phone}</span></div>
          )}
          {prospect.email && (
            <div style={styles.infoRow}><span style={styles.infoLabel}>Email</span><span>{prospect.email}</span></div>
          )}
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Club</span>
            <span>{prospect.clubId}</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Member Status</span>
            <span style={{ textTransform: 'capitalize' }}>{prospect.memberStatus || '—'}</span>
          </div>
          {daysSinceContact !== null && (
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Last Contact</span>
              <span style={{ color: daysSinceContact > 30 ? 'var(--yellow)' : 'var(--text)' }}>
                {daysSinceContact === 0 ? 'Today' : `${daysSinceContact}d ago`}
              </span>
            </div>
          )}
          {prospect.generatedDate && (
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Added</span>
              <span>{new Date(prospect.generatedDate).toLocaleDateString()}</span>
            </div>
          )}
        </div>
      </div>

      {/* Activation script suggestion */}
      {prospect.suggestedScript && (
        <div style={{
          marginTop: '1.25rem',
          background: 'rgba(168,85,247,0.08)',
          border: '1px solid rgba(168,85,247,0.25)',
          borderRadius: 8,
          padding: '1rem',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#a855f7', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              ⚡ {prospect.activationName} · Suggested Script
            </div>
            <button
              style={{ fontSize: 11, padding: '3px 10px', background: 'rgba(168,85,247,0.15)', color: '#a855f7', border: '1px solid rgba(168,85,247,0.3)', borderRadius: 4, cursor: 'pointer' }}
              onClick={() => {
                navigator.clipboard.writeText(prospect.suggestedScript.body);
              }}
            >
              Copy
            </button>
          </div>
          <pre style={{ fontSize: 12, color: 'var(--text)', whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0, lineHeight: 1.6 }}>
            {prospect.suggestedScript.body}
          </pre>
          {prospect.suggestedScript.whyContext && (
            <div style={{ marginTop: '0.5rem', fontSize: 11, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
              {prospect.suggestedScript.whyContext}
            </div>
          )}
        </div>
      )}

      {/* Contact history */}
      {prospect.history?.length > 0 && (
        <div style={{ marginTop: '1.25rem' }}>
          <div className="section-header">Contact History · {prospect.history.length}</div>
          {prospect.history.map((h, i) => (
            <div key={i} className="card" style={{ padding: '0.75rem 1rem', marginBottom: '0.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {h.sequenceNum && (
                    <span style={{
                      fontSize: 10, fontWeight: 700,
                      color: h.sequenceNum === 1 ? '#6b7280' : h.sequenceNum === 2 ? '#f97316' : '#ef4444',
                      textTransform: 'uppercase', letterSpacing: '0.04em',
                      background: h.sequenceNum === 1 ? '#6b728022' : h.sequenceNum === 2 ? '#f9731622' : '#ef444422',
                      border: `1px solid ${h.sequenceNum === 1 ? '#6b728044' : h.sequenceNum === 2 ? '#f9731644' : '#ef444444'}`,
                      borderRadius: 3, padding: '1px 6px',
                    }}>
                      {h.sequenceNum === 1 ? 'Initial' : `Follow-Up ${h.sequenceNum - 1}`}
                    </span>
                  )}
                  <span className="badge badge-blue">{h.outcome}</span>
                </div>
                <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{h.date}</span>
              </div>
              {h.contactMethod && (
                <p style={{ marginTop: '0.25rem', fontSize: 12, color: 'var(--text-secondary)' }}>
                  via {h.contactMethod}
                </p>
              )}
              {h.notes && (
                <p style={{ marginTop: '0.3rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  {h.notes}
                </p>
              )}
              {h.rep && (
                <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>{h.rep}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {(!prospect.history || prospect.history.length === 0) && (
        <div className="empty-state" style={{ padding: '1.5rem', marginTop: '1.25rem' }}>
          <p style={{ color: 'var(--text-secondary)' }}>No contact history yet.</p>
        </div>
      )}

      {/* DNC confirmation modal */}
      {showDNCConfirm && (
        <div className="modal-overlay" onClick={() => setShowDNCConfirm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-title" style={{ color: 'var(--red)' }}>⚠️ Do Not Contact</div>
            <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '1rem', lineHeight: 1.6 }}>
              This will permanently suppress <strong>{prospect.name}</strong> across ALL pipelines. They will never surface in any task list again. This cannot be undone.
            </p>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowDNCConfirm(false)}>Cancel</button>
              <button
                className="btn btn-primary"
                style={{ background: 'var(--red)', borderColor: 'var(--red)' }}
                onClick={() => {
                  setShowDNCConfirm(false);
                  setOutcomeModal(true);
                }}
              >
                Confirm — Add to DNC
              </button>
            </div>
          </div>
        </div>
      )}

      {outcomeModal && (
        <LeadOutcomeModal
          prospect={showDNCConfirm ? { ...prospect, _forceDNC: true } : prospect}
          onClose={() => setOutcomeModal(false)}
          onSaved={() => { setOutcomeModal(false); load(); }}
        />
      )}
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  contactBar: {
    display: 'flex', gap: '0.75rem', alignItems: 'center',
    padding: '0.5rem 0.75rem',
    background: 'var(--surface-2)', border: '1px solid var(--border)',
    borderRadius: '8px', marginBottom: '0.75rem',
  },
  scoreCard: {
    display: 'flex', gap: '1rem',
    background: 'var(--surface-2)', border: '1px solid var(--border)',
    borderRadius: '10px', padding: '1rem',
    marginBottom: '0.75rem',
  },
  scoreMain: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', minWidth: 70,
  },
  scoreNumber: {
    fontSize: '2rem', fontWeight: 800, color: 'var(--yellow)', lineHeight: 1,
  },
  scoreLabel: {
    fontSize: '0.65rem', color: 'var(--text-secondary)',
    textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '0.25rem',
  },
  scoreDivider: {
    width: 1, background: 'var(--border)', alignSelf: 'stretch',
  },
  scoreDetails: {
    flex: 1, display: 'flex', flexDirection: 'column', gap: '0.35rem',
  },
  scoreRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    fontSize: '0.82rem',
  },
  scoreKey: {
    color: 'var(--text-secondary)', fontSize: '0.75rem',
    textTransform: 'uppercase', letterSpacing: '0.04em',
  },
  scoreVal: {
    color: 'var(--text)',
  },
  infoCard: {
    background: 'var(--surface-2)', border: '1px solid var(--border)',
    borderRadius: '10px', padding: '0.75rem 1rem',
    marginBottom: '0.75rem',
  },
  infoGrid: {
    display: 'flex', flexDirection: 'column', gap: '0.4rem',
  },
  infoRow: {
    display: 'flex', justifyContent: 'space-between',
    fontSize: '0.83rem', padding: '0.25rem 0',
    borderBottom: '1px solid var(--border)',
  },
  infoLabel: {
    color: 'var(--text-secondary)', fontSize: '0.75rem',
    textTransform: 'uppercase', letterSpacing: '0.04em',
  },
};
